import React, { useEffect, useRef } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { BRAND } from '../../theme';
import { ENTRY_SECTIONS } from './sections';

interface EntryStepsProps {
  /** Index of the section currently in view */
  activeIndex: number;
  /** Sections whose required fields are all filled */
  completeIds: string[];
  onSelect: (sectionId: string) => void;
  /**
   * Phone layout: a single row of pills that sticks under the app bar while the
   * page scrolls, instead of the wide numbered stepper.
   */
  compact?: boolean;
  /** Distance from the top of the viewport the compact bar sticks at */
  stickyTop?: number;
}

/** Height of the compact bar, so callers can offset scroll targets below it. */
export const COMPACT_STEPS_HEIGHT = 52;

/**
 * A jump bar, not a wizard: every section stays on the page, this scrolls to one
 * and reflects whichever is currently in view.
 */
const EntrySteps: React.FC<EntryStepsProps> = ({
  activeIndex,
  completeIds,
  onSelect,
  compact,
  stickyTop = 0
}) => {
  const rowRef = useRef<HTMLDivElement | null>(null);

  // Keep the active pill in view as the page scrolls through the sections.
  useEffect(() => {
    if (!compact) return;
    const row = rowRef.current;
    const pill = row?.querySelector<HTMLElement>(`[data-step-index="${activeIndex}"]`);
    if (!row || !pill) return;
    const left = pill.offsetLeft - (row.clientWidth - pill.offsetWidth) / 2;
    row.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [activeIndex, compact]);

  if (compact) {
    return (
      <Box
        sx={{
          position: 'sticky',
          top: stickyTop,
          zIndex: 5,
          backgroundColor: 'background.paper',
          borderBottom: `1px solid ${BRAND.border}`,
          boxShadow: '0 4px 10px -8px rgba(15, 23, 42, 0.35)'
        }}
      >
        <Box
          ref={rowRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: COMPACT_STEPS_HEIGHT,
            px: 1.5,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' }
          }}
        >
          {ENTRY_SECTIONS.map((section, index) => {
            const active = index === activeIndex;
            const complete = completeIds.includes(section.id);
            return (
              <ButtonBase
                key={section.id}
                data-step-index={index}
                onClick={() => onSelect(section.id)}
                aria-current={active ? 'step' : undefined}
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  height: 36,
                  pl: 0.5,
                  pr: 1.5,
                  borderRadius: 18,
                  border: '1px solid',
                  borderColor: active ? BRAND.blue : BRAND.border,
                  backgroundColor: active ? '#EFF4FE' : 'background.paper',
                  transition: 'background-color 150ms, border-color 150ms'
                }}
              >
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    backgroundColor: complete || active ? BRAND.blue : '#F1F5F9',
                    color: complete || active ? '#fff' : BRAND.textMuted
                  }}
                >
                  {complete ? <CheckIcon sx={{ fontSize: 16 }} /> : index + 1}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: active ? 700 : 500,
                    color: active ? BRAND.navy : 'text.secondary',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {section.step}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0,
        px: { xs: 1, sm: 2 },
        py: 2,
        overflowX: 'auto'
      }}
    >
      {ENTRY_SECTIONS.map((section, index) => {
        const active = index === activeIndex;
        const complete = completeIds.includes(section.id);
        const reached = index <= activeIndex;

        return (
          <React.Fragment key={section.id}>
            {index > 0 && (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 16,
                  height: 2,
                  mt: '15px',
                  backgroundColor: reached ? BRAND.blue : BRAND.border
                }}
              />
            )}
            <ButtonBase
              onClick={() => onSelect(section.id)}
              sx={{
                flexDirection: 'column',
                gap: 0.75,
                px: 1,
                borderRadius: 1,
                flexShrink: 0
              }}
              aria-current={active ? 'step' : undefined}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 13,
                  fontWeight: 700,
                  border: '2px solid',
                  borderColor: active || complete ? BRAND.blue : BRAND.border,
                  backgroundColor: active || complete ? BRAND.blue : 'transparent',
                  color: active || complete ? '#fff' : BRAND.textMuted
                }}
              >
                {complete && !active ? <CheckIcon sx={{ fontSize: 18 }} /> : index + 1}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: active ? 700 : 500,
                  color: active ? BRAND.navy : 'text.secondary',
                  whiteSpace: 'nowrap'
                }}
              >
                {section.step}
              </Typography>
            </ButtonBase>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default EntrySteps;
